import { useEffect, useState } from "react";
import { useCurrentRapidData, useIsTraining } from "@rapid/store";
import { IMPROVE_DATA_READY_THRESHOLD } from "@rapid/constants/inference";
import { useDatabase } from "@rapid/providers/database/DatabaseProvider";

/**
 * Hook to watch the review batch count in real-time using Firestore listeners.
 *
 * Subscribes directly to the datasets collection filtered by rapidDataId,
 * then to the "rapid-review" batch subcollection. Both listeners are real-time,
 * so the count updates immediately when the background process creates the
 * dataset/batch or adds images — no polling or server API calls needed.
 */
export function useReviewBatchCount() {
    const currentRapidData = useCurrentRapidData();
    const rapidDataId = currentRapidData?.id;
    const workspaceId = currentRapidData?.workspaceId;
    const isTraining = useIsTraining();
    const [count, setCount] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const database = useDatabase();

    const newDataReady = !isTraining && (count ?? 0) >= IMPROVE_DATA_READY_THRESHOLD;

    useEffect(() => {
        if (!rapidDataId || !workspaceId) {
            setCount(null);
            return;
        }

        setIsLoading(true);
        let batchUnsub: (() => void) | null = null;
        let creationAttempted = false;

        // Listen for the dataset with this rapidDataId
        const datasetUnsub = database.onQuerySnapshot(
            "datasets",
            {
                where: [
                    ["owner", "==", workspaceId],
                    ["rapidDataId", "==", rapidDataId]
                ]
            },
            (datasetDocs) => {
                if (datasetDocs.length === 0) {
                    // Dataset doesn't exist yet — listener stays active and will
                    // fire again when the background process creates it
                    setCount(0);
                    setIsLoading(false);
                    return;
                }

                const datasetId = datasetDocs[0].id;

                // Clean up previous batch listener if dataset changed
                if (batchUnsub) {
                    batchUnsub();
                }

                // Listen for the "rapid-review" batch within this dataset
                batchUnsub = database.onQuerySnapshot(
                    `datasets/${datasetId}/batches`,
                    { where: [["name", "==", "rapid-review"]] },
                    (batchDocs) => {
                        if (batchDocs.length === 0) {
                            setCount(0);
                            if (!creationAttempted) {
                                creationAttempted = true;
                                // Use setDoc with a deterministic docId so concurrent
                                // hook instances converge on the same document instead
                                // of each creating a new auto-id doc.
                                database
                                    .setDoc(`datasets/${datasetId}/batches`, "rapid-review", {
                                        name: "rapid-review",
                                        images: 0
                                    })
                                    .catch((error) => {
                                        creationAttempted = false;
                                        console.warn(
                                            "[useReviewBatchCount] Failed to create rapid-review batch:",
                                            error
                                        );
                                    });
                            }
                        } else {
                            setCount(batchDocs[0].data().images ?? 0);
                        }
                        setIsLoading(false);
                    },
                    (error) => {
                        console.warn("[useReviewBatchCount] Batch listener error:", error);
                        setIsLoading(false);
                    }
                );
            },
            (error) => {
                console.warn("[useReviewBatchCount] Dataset listener error:", error);
                setIsLoading(false);
            }
        );

        return () => {
            datasetUnsub();
            if (batchUnsub) {
                batchUnsub();
            }
        };
    }, [rapidDataId, workspaceId, database]);

    return { count, isLoading, newDataReady };
}
